import cn from 'classnames';
import Image from 'next/image';

import ArticleBadges from './atoms/ArticleBadges';

import { getArticleBadges } from '@lib/article/getters';
import { getArticleImgUrl } from '@lib/image/getters';
import { Link } from '@lib/navigation';
import { WRPost } from '@utils/api/server';
import { WRAuthorRemapped } from '@utils/api/server/wr/post';
import { ArticleI } from 'types/article';
import { Author } from 'types/author';

interface ArticleTileLargeProps {
  className?: string;
  article: ArticleI | WRPost;
  lazyLoading?: boolean;
}

export default function ArticleTileLarge({ className, article, lazyLoading }: ArticleTileLargeProps) {
  const formattedDate = new Date(
    // @ts-expect-error -- dateGmt/date_gmt are not declared on ArticleI | WRPost
    article?.dateGmt || article?.date_gmt || 0,
  ).toLocaleDateString('pl-PL');

  if (!article) {
    return null;
  }

  return (
    <Link
      href={`/${article.slug}`}
      className={cn('group relative block overflow-hidden rounded-2xl md:aspect-[7/5]', className)}
    >
      <div className="relative flex min-h-64 w-full flex-col justify-end md:h-full">
        <Image
          src={getArticleImgUrl(article, 'large')}
          loading={!lazyLoading && typeof lazyLoading !== 'undefined' ? 'eager' : 'lazy'}
          width={700}
          height={500}
          alt={article.title}
          className="absolute h-full w-full rounded-t-2xl object-cover transition-transform duration-1000 group-hover:scale-105 md:rounded-b-2xl"
        />

        <ArticleBadges badges={getArticleBadges(article)} showBadges="always" absolute={true} />

        <div className="z-10 flex flex-col justify-end bg-gradient-to-t from-black/80 to-black/0 p-4 md:p-6 lg:h-1/2">
          <h3 className="text-xl font-medium text-white sm:text-2xl md:mb-3">{article.title}</h3>

          <div className="hidden items-center md:flex">
            <ArticleAuthor author={article.author} className="mr-2" />
            <span className="mr-2 text-premium-100">|</span>
            <p className="text-sm text-gray-200">{formattedDate}</p>
          </div>
        </div>
      </div>
      <div className="flex items-end justify-between rounded-b-2xl border border-t-0 border-premium-100 px-2 py-4 md:hidden dark:border-0 dark:bg-dark-500">
        <ArticleAuthor author={article.author} className="mr-2" />
        <p className="text-sm dark:text-white">{formattedDate}</p>
      </div>
    </Link>
  );
}

interface ArticleAuthorProps {
  className?: string;
  author: Author | WRAuthorRemapped | null;
}
const ArticleAuthor = ({ className, author }: ArticleAuthorProps) => {
  const authorName = author ? `${author.firstName} ${author.lastName}` : 'Guest';

  return (
    <div className={cn('flex items-center', className)}>
      <Image
        src={author?.avatar || '/static/default-avatar.png'}
        alt={authorName}
        loading="lazy"
        width={48}
        height={48}
        className="mr-1 h-12 w-12 rounded-full bg-premium-100"
      />
      <p className="ml-2 flex flex-col text-sm font-medium md:flex-row md:text-gray-200 dark:text-gray-200">
        <span className="text-premium-900 md:mr-2 md:text-premium-100 dark:text-orange-200">{authorName}</span>{' '}
        {author?.userCaption}
      </p>
    </div>
  );
};
